import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import ModalContext from 'contexts/ModalContext'
import HotelListingFilterPanel, { type HotelListingFilterPanelProps } from './HotelListingFilterPanel'

interface Props extends Omit<HotelListingFilterPanelProps, 'isMobile' | 'open' | 'onClose' | 'triggerRef'> {
  chipLabel: string
}

function HotelListingFilterDropdown({ label, chipLabel, options, selectedValues, onApply, single = false }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { value: open, toggle: toggleOpen, on: openPanel, off: close } = useToggle()
  const visibleOptions = useMemo(() => {
    const availableValues = new Set(options.map(option => option.value))
    return [...options, ...selectedValues.filter(value => !availableValues.has(value)).map(value => ({
      value,
      label: intl.formatMessage({
        defaultMessage: 'Selected filter: {value}',
        id: 'LLR65V53GgbVQLLv1aJV',
        description: 'Fallback label for a selected hotel filter absent from the current search facets.',
      }, { value }),
    }))]
  }, [intl, options, selectedValues])
  const toggle = useCallback(async() => {
    if (!isMobile) {
      toggleOpen()
      return
    }
    openPanel()
    try {
      await showModal<void>(resolve => (
        <HotelListingFilterPanel label={label} options={visibleOptions} selectedValues={selectedValues} onApply={onApply} single={single} isMobile open onClose={resolve} triggerRef={triggerRef} />
      ))
    } finally {
      close()
    }
  }, [close, isMobile, label, onApply, openPanel, selectedValues, showModal, single, toggleOpen, visibleOptions])
  const endIcon = useMemo(() => <DropdownChevron open={open} />, [open])
  return (
    <>
      <FilterChip ref={triggerRef} size="medium" selected={!!selectedValues.length} aria-pressed={!!selectedValues.length} aria-label={chipLabel} onClick={toggle} endIcon={endIcon}>
        {chipLabel}
      </FilterChip>
      {!isMobile && (
        <HotelListingFilterPanel label={label} options={visibleOptions} selectedValues={selectedValues} onApply={onApply} single={single} isMobile={false} open={open} onClose={close} triggerRef={triggerRef} />
      )}
    </>
  )
}
export default HotelListingFilterDropdown
